import { useCallback, useContext } from 'react';
import { AnalyticsBrowser } from '@segment/analytics-next';
import Cookies from 'js-cookie';

import { sendGA4Event } from 'app/lib/google';
import { AuthContext } from 'app/shared/context/Auth';

const SENSITIVE_QUERY_PARAMS = [
  'token',
  'reset_password_token',
  'ea_share_token',
  'email',
];

const sanitizeTrackingUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    SENSITIVE_QUERY_PARAMS.forEach((param) =>
      parsed.searchParams.delete(param)
    );
    return parsed;
  } catch {
    return null;
  }
};

export const useTrackAnalyticsEvent = (
  analytics: AnalyticsBrowser,
  eventsCalled: string[],
  setEventsCalled: (events: string[]) => void
) => {
  const { visitorType } = useContext(AuthContext);

  return useCallback(
    (
      eventDescription: string,
      options: {
        [key: string]: any;
      },
      trackOnce = false,
      eventIdentifier = ''
    ) => {
      if (trackOnce) {
        const eventIdentifierString = eventIdentifier.toString();
        if (eventsCalled.includes(eventDescription + eventIdentifierString)) {
          return;
        } else {
          setEventsCalled([
            ...eventsCalled,
            eventDescription + eventIdentifierString,
          ]);
        }
      }

      const cookies = Cookies.get();
      const {
        ttclid,
        tt_test_id,
        ScCid, // Snapchat cookie that we set based on query params
        _scid, // Snapchat cookie that may or may not have gotten automatically set
        _fbp, // Facebook browser id cookie, automatically set by the FB pixel script if/when it loads
        _fbc, // Facebook click id cookie, automatically set by the FB pixel script when a visitor arrives via FB ad click
      } = cookies;

      const ga4SessionKey = Object.keys(cookies).find((key: string) =>
        /^_ga_/.test(key)
      );
      const ga_session_id =
        ga4SessionKey &&
        cookies[ga4SessionKey]?.match(/GS\d\.\d\.(?<session_id>\d+)\..*/)?.[1];

      const analyticsProperties: { [key: string]: any } = {
        ...options,
        visitor_type: visitorType,
        ttclid,
        tt_test_id,
        ga_session_id,
        ScCid,
        _scid,
        fbp: _fbp,
        fbc: _fbc,
      };

      const sanitizedUrl = sanitizeTrackingUrl(window.location.href);

      analytics.track(eventDescription, analyticsProperties, {
        consent: {
          categoryPreferences: {
            necessary: window.Cookiebot?.consent?.necessary,
            preferences: window.Cookiebot?.consent?.preferences,
            statistics: window.Cookiebot?.consent?.statistics,
            marketing: window.Cookiebot?.consent?.marketing,
          },
        },
        ...(sanitizedUrl && {
          context: {
            page: {
              url: sanitizedUrl.toString(),
              search: sanitizedUrl.search,
            },
          },
        }),
      });
      // The Braze ID is only for Braze, so Google shouldn't receive it
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { braze_external_id, ...ga4Properties } = analyticsProperties;
      sendGA4Event(eventDescription, ga4Properties);
    },
    [analytics, visitorType, eventsCalled]
  );
};
